import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Checkbox, Row } from "@narmi/design_system";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { Card } from "@lending/components/Card/Card";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PlainButton } from "@lending/components/PlainButton/PlainButton";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useEvaluateAutoLoanApplicationQuery } from "@lending/hooks/queries/AutoLoanApplication/useEvaluateAutoLoanApplicationQuery";
import { useGetAutoLoanApplicationQuery } from "@lending/hooks/queries/AutoLoanApplication/useGetAutoLoanApplicationQuery";
import { useEvaluateCreditCardApplicationQuery } from "@lending/hooks/queries/CreditCardApplication/useEvaluateCreditCardApplicationQuery";
import { useGetCreditCardApplicationQuery } from "@lending/hooks/queries/CreditCardApplication/useGetCreditCardApplicationQuery";
import { useEvaluatePersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useEvaluatePersonalLoanApplicationQuery";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { useGetDisclosuresQuery } from "@lending/hooks/queries/useGetDisclosuresQuery";
import { usePatchApplicantConsentQuery } from "@lending/hooks/queries/usePatchApplicantConsentQuery";
import { useIsCoApplicantEnabled } from "@lending/hooks/useIsCoApplicantEnabled";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { ensureIovationScripts, getIovationDeviceId } from "@lending/lib/iovation";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./Disclosures.module.css";
import { getProductUuid } from "./getProductUuid";
import { InfoAlert } from "../components/InfoAlert";

function Disclosures() {
  const { l10n } = useLocalization();
  const { goBack, goNext, startOver } = useFlowNavigation();
  const { showErrorToast, showGenericErrorToast } = useToastsContext();
  const lendingApplicationID = useLendingApplicationID();
  useIsCoApplicantEnabled();

  const { data: applicantDetails } = useListApplicationApplicantDetailsQuery();
  const applicants = applicantDetails?.results ?? [];

  const productType = useProductType();
  const isAutoLoan = productType === "auto-loan";
  const isPersonalLoan = productType === "personal-loan";
  const isCreditCard = productType === "credit-card";

  const personalLoanApplicationQuery = useGetPersonalLoanApplicationQuery({
    enabled: isPersonalLoan,
  });
  const creditCardApplicationQuery = useGetCreditCardApplicationQuery({
    enabled: isCreditCard,
  });
  const autoLoanApplicationQuery = useGetAutoLoanApplicationQuery({
    enabled: isAutoLoan,
  });

  const applicationQuery = isAutoLoan
    ? autoLoanApplicationQuery
    : isPersonalLoan
      ? personalLoanApplicationQuery
      : creditCardApplicationQuery;

  const application = applicationQuery.data;

  // Scope disclosures to the application's selected product. Disclosures aren't
  // fetched until the product is known.
  const productUuid = getProductUuid(
    application?.product as { id?: string; uuid?: string } | string | undefined
  );
  const { disclosures } = useGetDisclosuresQuery(productUuid);
  const requiredMessage = l10n.getString("error-required", null, "Required");

  // Create dynamic zod schema based on disclosures
  const schema = useMemo(() => {
    if (!disclosures) return z.object({});

    const schemaObject: Record<string, z.ZodTypeAny> = {};
    disclosures.forEach((_, index) => {
      schemaObject[`disclosure_${index}`] = z.boolean().refine((val) => val === true, {
        message: requiredMessage,
      });
    });

    return z.object(schemaObject);
  }, [disclosures, requiredMessage]);

  // Create default values based on disclosures
  const defaultValues = useMemo(() => {
    if (!disclosures) return {};

    const values: Record<string, boolean> = {};
    disclosures.forEach((_, index) => {
      values[`disclosure_${index}`] = false;
    });

    return values;
  }, [disclosures]);

  const { handleSubmit, control } = useForm({
    defaultValues,
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const applicationStatusErrorMessage = l10n.getString(
    "disclosures-application-status-error",
    null,
    "We couldn't verify your application status. Please refresh the page and try again."
  );
  // Disclosures only load once the application's product is known, so there's no
  // separate pending check. A loaded application without a product is an error.
  const hasApplicationStateError = application ? !productUuid : applicationQuery.isError;
  const isPageReady =
    Boolean(applicants.length > 0 && disclosures && disclosures.length > 0) &&
    !hasApplicationStateError;

  useEffect(() => {
    ensureIovationScripts();
  }, []);

  useEffect(() => {
    if (hasApplicationStateError) {
      showErrorToast(applicationStatusErrorMessage);
    }
  }, [applicationStatusErrorMessage, hasApplicationStateError, showErrorToast]);

  const creditCardQuery = useEvaluateCreditCardApplicationQuery;
  const autoLoanQuery = useEvaluateAutoLoanApplicationQuery;
  const personalLoanQuery = useEvaluatePersonalLoanApplicationQuery;

  let handleQuery: typeof creditCardQuery | typeof autoLoanQuery | typeof personalLoanQuery;
  if (isAutoLoan) {
    handleQuery = autoLoanQuery;
  } else if (isPersonalLoan) {
    handleQuery = personalLoanQuery;
  } else {
    handleQuery = creditCardQuery;
  }

  const { mutateAsync: evaluateApplication, isPending: isEvaluating } = handleQuery({
    onSuccess: () => {
      goNext();
    },
    onError: (error) => {
      console.error("Error evaluating application:", error);
      showGenericErrorToast();
    },
  });

  const { mutateAsync: patchApplicantConsent, isPending: isPatchingConsent } =
    usePatchApplicantConsentQuery();

  async function evaluateApplicationWithDeviceId() {
    await evaluateApplication({
      params: {
        path: {
          uuid: lendingApplicationID,
        },
      },
      body: {
        device_id: getIovationDeviceId(),
      },
    });
  }

  async function onSubmit() {
    if (applicants.length === 0) {
      showGenericErrorToast();
      startOver();
      return;
    }

    if (applicants.every((applicant) => applicant.consent_accepted_at)) {
      if (!application) {
        showErrorToast(applicationStatusErrorMessage);
        return;
      }

      if (application.state === "creating") {
        await evaluateApplicationWithDeviceId();
        return;
      }

      goNext();
      return;
    }

    const currentTimestamp = new Date().toISOString();

    try {
      for (const applicant of applicants.filter((item) => !item.consent_accepted_at)) {
        await patchApplicantConsent({
          params: {
            path: {
              uuid: applicant.id,
            },
          },
          body: {
            consent_accepted_at: currentTimestamp,
          },
        });
      }
    } catch {
      // The mutation hook already surfaces an error toast via its onError
      // handler. Bail so the evaluate call does not fire; the consent PATCH
      // is idempotent, so a retry safely re-attempts any applicant whose
      // consent did not save.
      return;
    }

    await evaluateApplicationWithDeviceId();
  }

  return (
    <PageLayout>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "disclosures-heading",
            null,
            "Last step! Agree and see your rate"
          )}
          subheading={l10n.getString(
            "disclosures-subheading",
            null,
            "Please review the important disclosures below before checking the consent box."
          )}
        />
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Card className="margin--bottom--l">
            <div className={styles.disclosuresList}>
              {disclosures?.map((d, index) => (
                <div key={index}>
                  <div className={styles.checkbox}>
                    <Controller
                      name={`disclosure_${index}`}
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Checkbox
                          checked={value || false}
                          onChange={(e) => onChange(e.target.checked)}
                          label={d.checkbox_text}
                          error={error?.message}
                        />
                      )}
                    />
                  </div>
                  <ul>
                    {d.links.map((l, linkIndex) => (
                      <li key={linkIndex}>
                        <PlainButton
                          label={l.text}
                          onClick={() => {
                            window.open(l.url, "_blank", "noopener,noreferrer");
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>
          <div className="margin--bottom--xxl">
            <InfoAlert
              message={l10n.getString(
                "disclosures-alert",
                null,
                "Checking your rate will not affect your credit score."
              )}
            />
          </div>
          <Row alignItems="center" justifyContent="space-between">
            <BackButton onClick={goBack} />
            <Button
              label={l10n.getString("disclosures-submit-button", null, "See my offer")}
              type="submit"
              disabled={!isPageReady}
              isLoading={isEvaluating || isPatchingConsent}
            />
          </Row>
        </form>
      </PageContent>
    </PageLayout>
  );
}

export { Disclosures };
